import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Sparkles, Loader2, Presentation, RefreshCw, Edit, Eye, Download } from "lucide-react";
import SlideEditor from "@/components/SlideEditor";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCourseContext } from "@/contexts/CourseContext";
import { markFreeCourseDownloadUsed } from "@/lib/freeTier";

interface SlideDeck {
  id: string;
  skill_node_id: string;
  title: string;
  version: number;
  slide_deck_json: any;
  created_at: string;
}

interface SkillNode {
  id: string;
  title: string;
  description: string | null;
}

const ManageNodeSlides = () => {
  const { nodeId, projectId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setCourseContext, clearCourseContext } = useCourseContext();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [node, setNode] = useState<SkillNode | null>(null);
  const [slideDecks, setSlideDecks] = useState<SlideDeck[]>([]);
  const [editingDeck, setEditingDeck] = useState<SlideDeck | null>(null);
  const [previewDeck, setPreviewDeck] = useState<SlideDeck | null>(null);
  const [previewSlideIndex, setPreviewSlideIndex] = useState(0);
  const [courseName, setCourseName] = useState<string | null>(null);

  // Set context when node loads
  useEffect(() => {
    if (node && projectId) {
      setCourseContext({
        courseId: projectId,
        courseName: courseName,
        nodeId: node.id,
        nodeName: node.title,
        pageType: "node-slides",
      });
    }
    
    return () => {
      clearCourseContext();
    };
  }, [node, projectId, courseName]);

  useEffect(() => {
    if (nodeId) {
      fetchNodeAndSlides();
    }
  }, [nodeId]);

  // Fetch course name for context
  useEffect(() => {
    const fetchCourseName = async () => {
      if (projectId) {
        const { data } = await supabase
          .from("learning_projects")
          .select("title")
          .eq("id", projectId)
          .single();
        if (data) setCourseName(data.title);
      }
    };
    fetchCourseName();
  }, [projectId]);

  const fetchNodeAndSlides = async () => {
    setLoading(true);
    try {
      // Fetch node details
      const { data: nodeData, error: nodeError } = await supabase
        .from("skill_nodes")
        .select("*")
        .eq("id", nodeId)
        .single();

      if (nodeError) throw nodeError;
      setNode(nodeData);

      // Fetch slide decks
      const { data: slidesData, error: slidesError } = await supabase
        .from("generated_slide_decks")
        .select("*")
        .eq("skill_node_id", nodeId)
        .order("version", { ascending: false });

      if (slidesError) throw slidesError;
      setSlideDecks(slidesData || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error loading slides",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSlides = async () => {
    if (!nodeId) return;

    setGenerating(true);
    try {
      const { data, error } = await invokeFunction("generate-node-slides", {
        body: { 
          skillNodeId: nodeId,
          minSlides: 3,
          maxSlides: 8
        },
      });

      if (error) throw error;

      toast({
        title: "Slides generated!",
        description: "AI has created presentation slides for this node.",
      });
      
      fetchNodeAndSlides();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error generating slides",
        description: error.message,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleExportDeck = async (deckId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('export-slides-pptx', {
        body: { deckId }
      });

      if (error) throw error;

      if (data?.filePath) {
        const { data: fileData, error: downloadError } = await supabase.storage
          .from('slide-decks')
          .download(data.filePath);

        if (downloadError) throw downloadError;

        const url = URL.createObjectURL(fileData);
        const a = document.createElement('a');
        a.href = url;
        a.download = `slides-${deckId}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: "Success",
          description: "Slides exported and downloaded"
        });
        await markFreeCourseDownloadUsed();
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error exporting",
        description: error.message
      });
    }
  };

  const renderSlidePreview = (deck: SlideDeck) => {
    const slides = deck.slide_deck_json?.slides || [];
    const theme = deck.slide_deck_json?.theme || {
      backgroundColor: "#1a1a2e",
      titleColor: "#ffffff",
      textColor: "#e8e8e8",
      backgroundGradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
    };

    if (slides.length === 0) {
      return (
        <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
          <p className="text-muted-foreground text-sm">No slides</p>
        </div>
      );
    }

    const currentSlide = slides[previewSlideIndex] || slides[0];
    
    return (
      <div 
        className="aspect-video rounded-lg p-4 flex flex-col"
        style={{ 
          background: theme.backgroundGradient || theme.backgroundColor,
          color: theme.textColor
        }}
      >
        <h3 
          className="text-sm font-bold mb-2 line-clamp-1"
          style={{ color: theme.titleColor }}
        >
          {currentSlide.title}
        </h3>
        <div className="text-xs space-y-1 flex-1 overflow-hidden">
          {currentSlide.paragraph ? (
            <p className="line-clamp-3">{currentSlide.paragraph}</p>
          ) : (
            <ul>
              {(currentSlide.bullets || []).slice(0, 3).map((bullet: string, idx: number) => (
                <li key={idx} className="line-clamp-1">• {bullet}</li>
              ))}
              {(currentSlide.bullets || []).length > 3 && (
                <li className="text-muted-foreground">...</li>
              )}
            </ul>
          )}
        </div>
        <div className="text-xs text-center mt-2 opacity-60">
          {previewSlideIndex + 1} / {slides.length}
        </div>
      </div>
    );
  };

  if (editingDeck) {
    return (
      <SlideEditor
        deckId={editingDeck.id}
        initialDeck={editingDeck.slide_deck_json}
        onClose={() => setEditingDeck(null)}
        onSave={() => {
          setEditingDeck(null);
          fetchNodeAndSlides();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b sticky top-0 bg-background z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/project/${projectId}`)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Presentation className="h-5 w-5 text-primary" />
                Manage Slides
              </h1>
              <p className="text-sm text-muted-foreground">{node?.title}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchNodeAndSlides}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button
              onClick={handleGenerateSlides}
              disabled={generating}
            >
              {generating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {generating ? "Generating..." : "AI Generate New"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid gap-6">
          {/* Node Info */}
          <Card>
            <CardHeader>
              <CardTitle>{node?.title}</CardTitle>
              <CardDescription>
                {node?.description || "No description provided"}
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Slide Decks */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Slide Decks ({slideDecks.length})</h2>

            {slideDecks.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Presentation className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Slides Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Generate slides using AI to create a presentation for this lesson.
                  </p>
                  <Button onClick={handleGenerateSlides} disabled={generating}>
                    {generating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="mr-2 h-4 w-4" />
                    )}
                    Generate Slides with AI
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {slideDecks.map((deck) => (
                  <Card key={deck.id} className="overflow-hidden">
                    <div className="p-3">
                      {renderSlidePreview(deck)}
                    </div>
                    <CardHeader className="pt-0 pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{deck.title || deck.slide_deck_json?.title || "Untitled Deck"}</CardTitle>
                        <Badge variant="outline">v{deck.version}</Badge>
                      </div>
                      <CardDescription>
                        {deck.slide_deck_json?.slides?.length || 0} slides • 
                        Created {new Date(deck.created_at).toLocaleDateString()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => setEditingDeck(deck)}
                          className="flex-1"
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setPreviewDeck(deck)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleExportDeck(deck.id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* AI Generation Tip */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">AI-Powered Slide Generation</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Click "AI Generate New" to create a new version of slides based on the lesson content.
                    Each generation creates a new version, so you can compare different styles.
                    Use the visual editor to customize colors, images, and layout.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Preview Modal */}
      {previewDeck && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold">{previewDeck.slide_deck_json?.title || "Slide Preview"}</h2>
              <Button variant="ghost" size="sm" onClick={() => setPreviewDeck(null)}>
                Close
              </Button>
            </div>
            <div className="p-4">
              {previewDeck.slide_deck_json?.slides && (
                <>
                  <div 
                    className="aspect-video rounded-lg p-8 mb-4"
                    style={{ 
                      background: previewDeck.slide_deck_json.theme?.backgroundGradient || 
                                  previewDeck.slide_deck_json.theme?.backgroundColor || 
                                  "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                    }}
                  >
                    <h2 
                      className="text-3xl font-bold mb-6"
                      style={{ color: previewDeck.slide_deck_json.theme?.titleColor || "#ffffff" }}
                    >
                      {previewDeck.slide_deck_json.slides[previewSlideIndex]?.title}
                    </h2>
                    {previewDeck.slide_deck_json.slides[previewSlideIndex]?.paragraph ? (
                      <p 
                        className="text-lg leading-relaxed"
                        style={{ color: previewDeck.slide_deck_json.theme?.textColor || "#e8e8e8" }}
                      >
                        {previewDeck.slide_deck_json.slides[previewSlideIndex].paragraph}
                      </p>
                    ) : (
                      <ul 
                        className="text-lg space-y-3"
                        style={{ color: previewDeck.slide_deck_json.theme?.textColor || "#e8e8e8" }}
                      >
                        {(previewDeck.slide_deck_json.slides[previewSlideIndex]?.bullets || []).map((bullet: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-primary">•</span>
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setPreviewSlideIndex(Math.max(0, previewSlideIndex - 1))}
                      disabled={previewSlideIndex === 0}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Slide {previewSlideIndex + 1} of {previewDeck.slide_deck_json.slides.length}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPreviewSlideIndex(Math.min(previewDeck.slide_deck_json.slides.length - 1, previewSlideIndex + 1))}
                      disabled={previewSlideIndex === previewDeck.slide_deck_json.slides.length - 1}
                    >
                      Next
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageNodeSlides;

